import React from 'react';
import * as PropTypes from 'prop-types';

import BlogArticleCard from './BlogArticleCard';
import PageLayout from '../layout/PageLayout';
import Head from 'next/head';
import R from '../../lib/routes';

import './BlogArticlesPage.scss';
import LoadMore from '../widgets/LoadMore';
import { updatePageArticles, resetArticlesPage } from '../../actions/articles';
import { getPageArticles } from '../../selectors/articles';
import { fallbackImageInfo, createFrontendPageUrl } from '../../lib/selectors';
import { NextSeo } from 'next-seo';
import i18n from '../../translations/i18n';

class BlogArticlesPage extends React.Component {
  LAYOUT_CLASSNAME = {
    highlightLeft: 'highlight-left',
    highlightRight: 'highlight-right',
    twoColumns: 'two-columns',
    threeColumns: 'three-columns',
    oneColumn: 'one-column',
  };
  PAGE_NAME = R.blog;

  _mounted = false;

  static propTypes = {
    articlesLayout: PropTypes.object,
    showMobileLayout: PropTypes.bool,
    articles: PropTypes.array,
  };

  state = {
    articlesLayout: [],
    articles: [],
    showMobileLayout: false,
  };

  componentDidUpdate(prevProps) {
    // Update list of articles
    if (prevProps !== this.props) {
      updatePageArticles(this.PAGE_NAME, { articles: this.props.articles, page: this.props.page });
      const articles = getPageArticles(this.PAGE_NAME, this.props.page);
      this.setState({ articles }, () => {
        // Rerender layout
        this.setLayout(this.state.articles);
      });
    }
  }

  setDesktopArticleLayout(articles) {
    if (!articles.length) return { sorted: [], layout: { className: null, arr: [] } };
    const { sorted } = articles.reduce(
      (obj, article, index) => {
        obj.layout.arr.push(article);
        const first = obj.layout.arr[0] && obj.layout.arr[0].highlighted;
        const second = obj.layout.arr[1] && obj.layout.arr[1].highlighted;
        const initialLayout = { className: null, arr: [] };
        const isLastArticle = articles.length - 1 === index;

        if (obj.layout.arr.length === 2) {
          // // Two columns
          if (first && second) {
            obj.layout.className = this.LAYOUT_CLASSNAME.twoColumns;
          }
          // // Highlight right
          if (!first && second) {
            obj.layout.className = this.LAYOUT_CLASSNAME.highlightRight;
          }
          // Highlight left
          if (first && !second) {
            obj.layout.className = this.LAYOUT_CLASSNAME.highlightLeft;
          }
          // Check for new article and then decide on pushing new layout
          if (!first && !second) {
            if (isLastArticle) {
              obj.layout.className = this.LAYOUT_CLASSNAME.twoColumns;
            } else {
              return obj;
            }
          }

          // Push new layout
          obj.sorted.push(obj.layout);
          obj.layout = initialLayout;
          return obj;
        }

        if (obj.layout.arr.length === 3) {
          if (!first && !second) {
            if (!article.highlighted) {
              // Three columns
              obj.layout.className = this.LAYOUT_CLASSNAME.threeColumns;
              obj.sorted.push(obj.layout);
              obj.layout = initialLayout;
            } else {
              // Two columns
              obj.layout.className = this.LAYOUT_CLASSNAME.twoColumns;
              obj.sorted.push({ ...obj.layout, arr: obj.layout.arr.splice(0, 2) });
            }
            return obj;
          }
        }
        if (isLastArticle && obj.layout.arr.length === 1) {
          obj.layout.className = this.LAYOUT_CLASSNAME.oneColumn;
          obj.sorted.push(obj.layout);
        }
        return obj;
      },
      { sorted: [], layout: { className: null, arr: [] } }
    );
    return sorted;
  }

  setMobileArticleLayout(articles) {
    if (!articles.length) return { sorted: [], layout: { className: null, arr: [] } };

    const { sorted } = articles.reduce(
      (obj, article, index) => {
        obj.layout.arr.push(article);
        const first = obj.layout.arr[0] && obj.layout.arr[0].highlighted;
        const second = obj.layout.arr[1] && obj.layout.arr[1].highlighted;
        const initialLayout = { className: null, arr: [] };

        if (obj.layout.arr.length === 2) {
          // Two columns
          if ((first && second) || (!first && !second)) {
            obj.layout.className = this.LAYOUT_CLASSNAME.twoColumns;
          }
          // Highlight right
          if (!first && second) {
            obj.layout.className = this.LAYOUT_CLASSNAME.highlightRight;
          }
          // Highlight left
          if (first && !second) {
            obj.layout.className = this.LAYOUT_CLASSNAME.highlightLeft;
          }

          // Push new layout
          obj.sorted.push(obj.layout);
          obj.layout = initialLayout;
          return obj;
        }
        const isLastArticle = articles.length - 1 === index;
        if (isLastArticle && obj.layout.arr.length === 1) {
          obj.layout.className = this.LAYOUT_CLASSNAME.oneColumn;
          obj.sorted.push(obj.layout);
        }
        return obj;
      },
      { sorted: [], layout: { className: null, arr: [] } }
    );
    return sorted;
  }

  onResize = () => {
    if (window.innerWidth >= 1000) {
      this._mounted &&
        this.state.showMobileLayout &&
        this.setState({
          articlesLayout: this.setDesktopArticleLayout(this.props.articles),
          showMobileLayout: false,
        });
    } else {
      this._mounted &&
        !this.state.showMobileLayout &&
        this.setState({
          articlesLayout: this.setMobileArticleLayout(this.props.articles),
          showMobileLayout: true,
        });
    }
  };

  setLayout(articles) {
    if (window.innerWidth >= 1000) {
      this.setState({
        articlesLayout: this.setDesktopArticleLayout(articles),
        showMobileLayout: false,
      });
    } else {
      this.setState({
        articlesLayout: this.setMobileArticleLayout(articles),
        showMobileLayout: true,
      });
    }
  }

  componentDidMount() {
    this._mounted = true;
    updatePageArticles(this.PAGE_NAME, { articles: this.props.articles, page: this.props.page });
    const articles = getPageArticles(this.PAGE_NAME, this.props.page);
    this.setState({ articles }, () => {
      this.setLayout(this.state.articles);
    });
    window.addEventListener('resize', this.onResize, false);
  }

  componentWillUnmount() {
    this._mounted = false;
    resetArticlesPage(this.PAGE_NAME);
    window.removeEventListener('resize', this.onResize, false);
  }

  render() {
    const { page, totalPages, ...passProps } = this.props;

    const articlesLayout = !this.state.articlesLayout.length
      ? null
      : this.state.articlesLayout.map((layout, i) => {
          return (
            <div className={layout.className} key={i}>
              {layout.arr.map(article => {
                return <BlogArticleCard key={article.slug} article={article} />;
              })}
            </div>
          );
        });

    return (
      <>
        <Head>
          <meta name="description" content={i18n.blogpageDescription} key="description" />
          <title>{i18n.blogpageSEOTitle}</title>
        </Head>
        <NextSeo
          openGraph={{
            url: createFrontendPageUrl(R.blog),
            locale: 'en_US',
            title: i18n.blogpageSEOTitle,
            description: i18n.blogpageDescription,
            type: 'website',
            images: [fallbackImageInfo],
            site_name: i18n.blogpageSiteName,
          }}
        />

        <PageLayout
          title={i18n.blogpageTitle}
          className="BlogArticlesPage"
          subTitle={i18n.blogpageDescription}
          {...passProps}
        >
          {articlesLayout && (
            <>
              <div className="layout-wrapper">{articlesLayout}</div>
              <LoadMore totalPages={totalPages} page={page} route={R.blog} />
            </>
          )}
        </PageLayout>
      </>
    );
  }
}

BlogArticlesPage.propTypes = {
  articles: PropTypes.array,
  page: PropTypes.number,
  totalPages: PropTypes.number,
};

export default BlogArticlesPage;
